import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Pencil, PlugZap, Plus, Star, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, type StorageBackend } from "../api";
import { EmptyState, ErrorNotice, LoadingRows, Status } from "../components";

const providerNames: Record<string, string> = {
  tencent_cos: "腾讯云 COS",
  aliyun_oss: "阿里云 OSS",
  huawei_obs: "华为云 OBS",
  volcengine_tos: "火山引擎 TOS",
  s3_compatible: "自定义 S3 / MinIO",
};

const credentialLabels: Record<string, { accessKey: string; secretKey: string }> = {
  tencent_cos: { accessKey: "SecretId", secretKey: "SecretKey" },
  aliyun_oss: { accessKey: "AccessKey ID", secretKey: "AccessKey Secret" },
  huawei_obs: { accessKey: "Access Key ID", secretKey: "Secret Access Key" },
  volcengine_tos: { accessKey: "Access Key", secretKey: "Secret Key" },
  s3_compatible: { accessKey: "Access Key ID", secretKey: "Secret Access Key" },
};

const storageClassOptions: Record<string, Array<{ value: string; label: string }>> = {
  tencent_cos: [
    { value: "STANDARD_IA", label: "低频访问（测试时自动匹配单/多 AZ）" },
    { value: "INTELLIGENT_TIERING", label: "智能分层（INTELLIGENT_TIERING）" },
    { value: "MAZ_STANDARD_IA", label: "多可用区低频（MAZ_STANDARD_IA）" },
    { value: "STANDARD", label: "标准存储（STANDARD）" },
    { value: "MAZ_STANDARD", label: "多可用区标准（MAZ_STANDARD）" },
    { value: "MAZ_INTELLIGENT_TIERING", label: "多可用区智能分层（MAZ_INTELLIGENT_TIERING）" },
  ],
  s3_compatible: [
    { value: "STANDARD_IA", label: "低频访问（STANDARD_IA，推荐）" },
    { value: "INTELLIGENT_TIERING", label: "智能分层（INTELLIGENT_TIERING）" },
    { value: "STANDARD", label: "标准存储（STANDARD）" },
    { value: "DEFAULT", label: "使用 Bucket 默认策略（兼容 MinIO）" },
  ],
};

type EditorState = { mode: "create" } | { mode: "edit"; item: StorageBackend };
type ProbeResult = { put_ms: number; head_ms: number; get_ms: number; delete_ms: number; storage_class: string | null; storage_class_adapted: boolean };
type Feedback = { tone: "positive" | "neutral"; message: string };

export function StoragePage({ role }: { role: string }) {
  const queryClient = useQueryClient();
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const backends = useQuery({ queryKey: ["storage"], queryFn: () => api<StorageBackend[]>("/admin/v1/storage/backends") });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["storage"] });
  const save = useMutation({
    mutationFn: (body: unknown) => api<StorageBackend>("/admin/v1/storage/backends", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: (item) => {
      setEditor(null);
      setFeedback({ tone: "neutral", message: `${item.name} 已保存，请测试连接后再投入使用。` });
      refresh();
    },
  });
  const probe = useMutation({
    mutationFn: (item: StorageBackend) => api<ProbeResult>(`/admin/v1/storage/backends/${item.id}/versions/${item.version}/validate`, { method: "POST", body: JSON.stringify({ reason: "控制台测试存储连接" }) }),
    onSuccess: (result, item) => {
      const adapted = result.storage_class_adapted ? `已自动适配输出存储类型为 ${result.storage_class}。` : "";
      setFeedback({ tone: "positive", message: `${item.name} 连接正常。${adapted}写入 ${result.put_ms}ms · 读取 ${result.get_ms}ms · 删除 ${result.delete_ms}ms` });
      refresh();
    },
  });
  const setDefault = useMutation({
    mutationFn: (item: StorageBackend) => api<StorageBackend>(`/admin/v1/storage/backends/${item.id}/versions/${item.version}/default`, { method: "POST", body: JSON.stringify({ reason: "控制台设置系统默认存储" }) }),
    onSuccess: (item) => {
      setFeedback({ tone: "positive", message: `${item.name} 已设为默认存储，新任务将使用该配置。` });
      refresh();
    },
  });
  const canWrite = role === "ADMIN";
  const error = backends.error ?? save.error ?? probe.error ?? setDefault.error;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const editing = editor?.mode === "edit" ? editor.item : null;
    const storageDirectory = normalizeDirectory(String(data.get("storage_directory") ?? "outputs"));
    const accessKey = String(data.get("access_key_id") ?? "").trim();
    const secretKey = String(data.get("secret_access_key") ?? "");
    const securityToken = String(data.get("security_token") ?? "");
    save.mutate({
      backend_id: editing?.id ?? String(data.get("backend_id") ?? ""),
      name: String(data.get("name") ?? ""),
      provider: String(data.get("provider") ?? ""),
      endpoint: String(data.get("endpoint") ?? ""),
      public_download_endpoint: String(data.get("public_download_endpoint") ?? ""),
      region: String(data.get("region") ?? "") || null,
      container: String(data.get("container") ?? ""),
      access_key_id: accessKey || null,
      secret_access_key: secretKey || null,
      security_token: securityToken || null,
      reuse_credentials_from_version: editing && !accessKey && !secretKey && !securityToken ? editing.version : null,
      base_prefix: "",
      upload_prefix: "uploads",
      output_prefix: storageDirectory,
      date_partition_pattern: String(data.get("date_partition_pattern") ?? "yyyy/MM/dd"),
      partition_timezone: String(data.get("partition_timezone") ?? "Asia/Shanghai"),
      result_url_ttl_seconds: Number(data.get("result_url_ttl_seconds")),
      provider_config: {
        path_style: data.get("path_style") === "on",
        allow_insecure_http: data.get("allow_insecure_http") === "on",
        ...(data.has("storage_class") ? { storage_class: String(data.get("storage_class")) } : {}),
      },
      reason: editing ? "控制台编辑存储配置" : "控制台新增存储配置",
    });
  };

  return <div className="page">
    <header className="page-heading storage-heading">
      <div><p className="eyebrow">CONFIGURATION / STORAGE</p><h1>存储配置</h1><p>统一管理输出存储。保存后先测试连接，再设为系统默认。</p></div>
      {canWrite && <button className="button button--primary" onClick={() => { setEditor({ mode: "create" }); setFeedback(null); }}><Plus size={17} />新增存储</button>}
    </header>

    {error && <ErrorNotice error={error} />}
    {feedback && <div className={`notice notice--${feedback.tone}`} aria-live="polite">{feedback.tone === "positive" && <Check size={17} />}{feedback.message}</div>}

    {editor && <StorageEditor key={editor.mode === "edit" ? `${editor.item.id}:${editor.item.version}` : "create"} editor={editor} pending={save.isPending} onClose={() => setEditor(null)} onSubmit={submit} />}

    <section className="section-block section-block--first">
      <div className="section-heading"><div><p className="eyebrow">STORAGE BACKENDS</p><h2>已配置存储</h2></div><span className="section-heading__meta">{backends.data?.length ?? 0} 项</span></div>
      {backends.isPending ? <LoadingRows columns={5} /> : backends.data?.length ? <div className="table-wrap"><table className="responsive-table storage-table"><thead><tr><th>存储</th><th>服务商 / Bucket</th><th>存储目录</th><th>连接状态</th><th>默认</th><th aria-label="操作" /></tr></thead><tbody>{backends.data.map((item) => {
        const probing = probe.isPending && probe.variables?.id === item.id;
        const defaulting = setDefault.isPending && setDefault.variables?.id === item.id;
        return <tr key={item.id}>
          <td data-label="存储"><strong>{item.name}</strong><small>{item.id}</small></td>
          <td data-label="服务商 / Bucket">{providerNames[item.provider] ?? item.provider}<small>{item.region ?? "默认区域"} · {item.container}</small></td>
          <td data-label="存储目录"><code>{storageDirectory(item)}/</code><small>{partitionDescription(item.date_partition_pattern)} · {storageClassLabel(item)}</small></td>
          <td data-label="连接状态"><Status value={item.status} />{item.is_default && item.default_version !== item.version && <small>修改待测试，线上仍使用上次配置</small>}</td>
          <td data-label="默认">{item.is_default ? <span className="default-mark"><Star size={14} fill="currentColor" />系统默认</span> : <span className="muted">—</span>}</td>
          <td className="responsive-table__actions"><div className="row-actions storage-actions">
            {canWrite && <button className="button button--small" disabled={probing} onClick={() => { setFeedback(null); probe.mutate(item); }}><PlugZap size={15} />{probing ? "测试中…" : "测试连接"}</button>}
            {canWrite && <button className="button button--small button--quiet" onClick={() => { setEditor({ mode: "edit", item }); setFeedback(null); }}><Pencil size={15} />编辑</button>}
            {canWrite && !item.is_default && <button className="button button--small button--quiet" disabled={item.status !== "ACTIVE" || defaulting} title={item.status !== "ACTIVE" ? "测试连接成功后才能设为默认" : undefined} onClick={() => { setFeedback(null); setDefault.mutate(item); }}><Star size={15} />{defaulting ? "设置中…" : "设为默认"}</button>}
          </div></td>
        </tr>;
      })}</tbody></table></div> : <EmptyState title="还没有存储配置">新增存储并完成连接测试，即可设置为系统默认。</EmptyState>}
    </section>
  </div>;
}

function StorageEditor({ editor, pending, onClose, onSubmit }: { editor: EditorState; pending: boolean; onClose: () => void; onSubmit: (event: FormEvent<HTMLFormElement>) => void }) {
  const item = editor.mode === "edit" ? editor.item : null;
  const providerConfig = item?.provider_config ?? {};
  const [provider, setProvider] = useState(item?.provider ?? "tencent_cos");
  const [storageClass, setStorageClass] = useState(
    typeof providerConfig.storage_class === "string"
      ? providerConfig.storage_class
      : item?.provider === "s3_compatible"
        ? "DEFAULT"
        : defaultStorageClass(item?.provider ?? "tencent_cos"),
  );
  const labels = credentialLabels[provider] ?? credentialLabels.s3_compatible;
  return <section className="storage-editor" aria-labelledby="storage-editor-title">
    <div className="storage-editor__intro"><div><p className="eyebrow">{item ? "EDIT STORAGE" : "NEW STORAGE"}</p><h2 id="storage-editor-title">{item ? `编辑 ${item.name}` : "新增存储"}</h2><p>{item ? "保存后生成待测试配置，当前线上任务不会受到影响。" : "填写连接信息和目录规则，保存后再执行连接测试。"}</p></div><button type="button" className="icon-button" aria-label="关闭编辑器" onClick={onClose}><X size={19} /></button></div>
    <form className="storage-form" key={item ? `${item.id}:${item.version}` : "new"} onSubmit={onSubmit}>
      <Field label="存储 ID"><input name="backend_id" required readOnly={Boolean(item)} defaultValue={item?.id ?? ""} placeholder="storage-cn-shanghai-primary" /></Field>
      <Field label="显示名称"><input name="name" required defaultValue={item?.name ?? ""} placeholder="上海生产输出" /></Field>
      <Field label="服务商"><select name="provider" value={provider} onChange={(event) => { const nextProvider = event.target.value; setProvider(nextProvider); setStorageClass(defaultStorageClass(nextProvider)); }}>{Object.entries(providerNames).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></Field>
      <Field label="区域"><input name="region" defaultValue={item?.region ?? ""} placeholder="ap-shanghai" /></Field>
      <Field label="API Endpoint" wide><input name="endpoint" type="url" required defaultValue={item?.endpoint ?? ""} placeholder="https://cos.ap-shanghai.myqcloud.com" /></Field>
      <Field label="外网下载 Endpoint" wide><input name="public_download_endpoint" type="url" required defaultValue={item?.public_download_endpoint ?? ""} placeholder="https://cos.ap-shanghai.myqcloud.com" /></Field>
      <Field label="Bucket" hint={inheritsBucketStorageClass(provider) ? "新上传文件继承 Bucket 的存储类型；如需低频，请先在云平台配置 Bucket。" : undefined}><input name="container" required defaultValue={item?.container ?? ""} /></Field>
      <Field label={labels.accessKey} hint={item?.credentials_configured ? "已配置；与 Secret 都留空将沿用现有凭证" : undefined}><input name="access_key_id" autoComplete="off" required={!item} placeholder={item ? "留空保持不变" : `请输入 ${labels.accessKey}`} /></Field>
      <Field label={labels.secretKey} hint={item?.credentials_configured ? "Secret 不会从服务端回显" : undefined}><input name="secret_access_key" type="password" autoComplete="new-password" required={!item} placeholder={item ? "留空保持不变" : `请输入 ${labels.secretKey}`} /></Field>
      <Field label="临时安全令牌（可选）" wide><input name="security_token" type="password" autoComplete="new-password" placeholder="仅使用临时凭证时填写" /></Field>

      <details className="storage-form__advanced">
        <summary><span>目录与高级设置</span><ChevronDown size={17} /></summary>
        <div className="storage-form__advanced-grid">
          <Field label="存储目录"><input name="storage_directory" defaultValue={item ? storageDirectory(item) : "outputs"} placeholder="例如：transcode/prod" required /></Field>
          <Field label="日期分区"><select name="date_partition_pattern" defaultValue={item?.date_partition_pattern ?? "yyyy/MM/dd"}><option value="none">不分区</option><option value="yyyy/MM">年 / 月</option><option value="yyyy/MM/dd">年 / 月 / 日</option></select></Field>
          {!inheritsBucketStorageClass(provider) && <Field label="输出存储类型" hint={storageClassHint(provider)}><select name="storage_class" value={storageClass} onChange={(event) => setStorageClass(event.target.value)}>{(storageClassOptions[provider] ?? storageClassOptions.s3_compatible).map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select></Field>}
          <Field label="分区时区"><input name="partition_timezone" defaultValue={item?.partition_timezone ?? "Asia/Shanghai"} required /></Field>
          <Field label="下载 URL 有效期（秒）" hint="填 0 表示 Bucket 已开启公开读，返回不带签名的公开 URL；系统不会修改 Bucket 权限。"><input name="result_url_ttl_seconds" type="number" min="0" max="604800" defaultValue={item?.result_url_ttl_seconds ?? 3600} required /></Field>
          <label className="check"><input name="path_style" type="checkbox" defaultChecked={providerConfig.path_style === true} />使用 Path-style 地址</label>
          <label className="check"><input name="allow_insecure_http" type="checkbox" defaultChecked={providerConfig.allow_insecure_http === true} />仅本地开发允许 HTTP</label>
        </div>
      </details>
      <div className="storage-form__actions"><button type="button" className="button button--quiet" onClick={onClose}>取消</button><button className="button button--primary" disabled={pending}>{pending ? "正在保存…" : item ? "保存修改" : "保存并继续"}</button></div>
    </form>
  </section>;
}

function Field({ label, hint, wide, children }: { label: string; hint?: string; wide?: boolean; children: React.ReactNode }) {
  return <label className={wide ? "field field--wide" : "field"}><span>{label}</span>{children}{hint && <small className="field__hint">{hint}</small>}</label>;
}

function storageDirectory(item: StorageBackend) {
  return normalizeDirectory([item.base_prefix, item.output_prefix].filter(Boolean).join("/"));
}

function normalizeDirectory(value: string) {
  return value.trim().split("/").filter(Boolean).join("/");
}

function partitionDescription(pattern: string) {
  if (pattern === "none") return "文件直接写入该目录";
  return `其下按 ${pattern} 自动分目录`;
}

function defaultStorageClass(provider: string) {
  return storageClassOptions[provider]?.[0]?.value ?? "DEFAULT";
}

function inheritsBucketStorageClass(provider: string) {
  return provider === "aliyun_oss" || provider === "volcengine_tos" || provider === "huawei_obs";
}

function storageClassHint(provider: string) {
  if (provider === "tencent_cos") return "测试连接时会实际写入探针，并自动匹配 Bucket 的单/多 AZ 存储类型。低频至少按 30 天、64KB 计费，下载有取回费。";
  return "MinIO 或部分自定义 S3 不支持低频类型时，请选择 Bucket 默认策略。";
}

function storageClassLabel(item: StorageBackend) {
  const configured = typeof item.provider_config.storage_class === "string" ? item.provider_config.storage_class : null;
  if (inheritsBucketStorageClass(item.provider)) return configured ? `旧版显式指定：${configured}` : "继承 Bucket 存储类型";
  const value = configured ?? (item.provider === "s3_compatible" ? "DEFAULT" : defaultStorageClass(item.provider));
  if (value === "DEFAULT") return "Bucket 默认存储";
  const option = (storageClassOptions[item.provider] ?? []).find((candidate) => candidate.value === value);
  return option?.label.split("（")[0] ?? value;
}
